import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import styles from './BrowserUseKeyPanel.module.css';

/**
 * BrowserUseKeyPanel — modal for self-service Browser Use API key entry.
 *
 * Every logged-in user gets one. Talks to /api/auth/me/browser-use-key
 * (GET / PATCH / DELETE). The full key is NEVER returned to the browser —
 * the panel only sees `{has_key, last4}` and shows a masked preview.
 *
 * Browser Use issues keys at https://cloud.browser-use.com/settings?tab=api-keys&new=1
 * Each user signs up + creates their own key, then pastes it here so usage is
 * attributed to their account.
 */

interface BrowserUseKeyPanelProps {
  onClose: () => void;
}

interface KeyStatus {
  has_key: boolean;
  last4: string | null;
}

const TOKEN_KEY = 'foreman-auth-token';
const SIGNUP_URL = 'https://cloud.browser-use.com/settings?tab=api-keys&new=1';

function authHeader(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY) || '';
  return { Authorization: `Bearer ${token}` };
}

async function readJsonOrThrow(res: Response): Promise<unknown> {
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data as { error?: string }).error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

interface ToastMsg { text: string; kind: 'ok' | 'err' }

export function BrowserUseKeyPanel({ onClose }: BrowserUseKeyPanelProps) {
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMsg | null>(null);

  const [editing, setEditing] = useState(false);
  const [draftKey, setDraftKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const showToast = useCallback((text: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/me/browser-use-key', { headers: authHeader() });
      const data = (await readJsonOrThrow(res)) as KeyStatus;
      setStatus(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load key status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Esc to close (but only when not actively editing).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editing) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editing, onClose]);

  const handleSave = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const key = draftKey.trim();
    if (!key) {
      showToast('Paste your key first', 'err');
      return;
    }
    if (key.length < 4) {
      showToast('Key must be at least 4 chars', 'err');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/auth/me/browser-use-key', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ key }),
      });
      const data = (await readJsonOrThrow(res)) as KeyStatus;
      setStatus(data);
      setDraftKey('');
      setEditing(false);
      showToast('Key saved', 'ok');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Save failed', 'err');
    } finally {
      setSaving(false);
    }
  }, [draftKey, showToast]);

  const handleClear = useCallback(async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Clear your Browser Use API key?');
    if (!ok) return;
    setClearing(true);
    try {
      const res = await fetch('/api/auth/me/browser-use-key', {
        method: 'DELETE',
        headers: authHeader(),
      });
      const data = (await readJsonOrThrow(res)) as KeyStatus;
      setStatus(data);
      showToast('Key cleared', 'ok');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Clear failed', 'err');
    } finally {
      setClearing(false);
    }
  }, [showToast]);

  return (
    <div className={styles.backdrop} onClick={() => !editing && onClose()}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Browser Use API key"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>Browser Use API key</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            disabled={editing && saving}
          >
            {'\u2715'}
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.help}>
            Browser Use lets Foreman drive a real browser to do things like pull
            photos from BuilderTrend or look up supplier specs. Each person
            sets their own key so usage is attributed to their own Browser Use
            account.{' '}
            <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">
              Create a key at cloud.browser-use.com{' '}
              {'\u2192'}
            </a>{' '}
            (sign up free, click <em>Create API key</em>, copy the value
            starting with <code>bu_</code>).
          </p>

          {error && <div className={styles.err}>{error}</div>}

          {loading ? (
            <div className={styles.loading}>Loading{'\u2026'}</div>
          ) : (
            <>
              <div className={styles.keyRow}>
                <div>
                  <div className={styles.keyLabel}>Your key</div>
                  {status?.has_key && status.last4 ? (
                    <div className={styles.keyValue}>
                      {/* 20 dots + last4. Visually approximates the masked
                          length without revealing the real character count. */}
                      {'\u00B7'.repeat(20)}
                      {status.last4}
                    </div>
                  ) : (
                    <div className={styles.keyValueDim}>(no key set)</div>
                  )}
                </div>
                <div className={styles.keyActions}>
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={() => { setEditing(true); setDraftKey(''); }}
                    disabled={editing}
                  >
                    {status?.has_key ? 'Replace key' : 'Set key'}
                  </button>
                  {status?.has_key && (
                    <button
                      type="button"
                      className={`${styles.smallBtn} ${styles.dangerBtn}`}
                      onClick={handleClear}
                      disabled={clearing || editing}
                    >
                      {clearing ? 'Clearing\u2026' : 'Clear'}
                    </button>
                  )}
                </div>
              </div>

              {editing && (
                <form className={styles.editForm} onSubmit={handleSave}>
                  <textarea
                    className={styles.input}
                    placeholder="Paste your bu_... key here"
                    value={draftKey}
                    onChange={(e) => setDraftKey(e.target.value)}
                    autoFocus
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      className={styles.cancelBtn}
                      onClick={() => { setEditing(false); setDraftKey(''); }}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={styles.primaryBtn}
                      disabled={saving || !draftKey.trim()}
                    >
                      {saving ? 'Saving\u2026' : 'Save key'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>

        {toast && (
          <div className={`${styles.toast} ${toast.kind === 'err' ? styles.toastErr : ''}`}>
            {toast.text}
          </div>
        )}
      </div>
    </div>
  );
}
